import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface WireframeShaderPlanetProps {
  scale?: number;
  quaternion?: THREE.Quaternion;
}

const WireframeShaderPlanet: React.FC<WireframeShaderPlanetProps> = ({
  scale = 1,
  quaternion
}) => {
  const nodesRef = useRef<THREE.Points>(null);
  const connectionsRef = useRef<THREE.LineSegments>(null);
  const timeRef = useRef(0);

  const sharedShaderCode = `
    uniform float uTime;
    uniform float uPulseSpeed;
    float getPulseIntensity(vec3 worldPos) {
        // Trouver les limites Y de la géométrie (approximation)
        float minY = -.5;
        float maxY = .25;
        float heightRange = maxY - minY;
        
        // Normaliser la position Y entre 0 (bas) et 1 (haut)
        float normalizedY = (worldPos.y - minY) / heightRange;
        
        // Créer une onde qui monte du bas vers le haut
        float waveDuration = 3.5;
        float timeSinceClick = mod(uTime, waveDuration);
        
        // Position de l'onde (0 = bas, 1 = haut)
        float wavePosition = timeSinceClick / waveDuration;
        
        // Distance entre la position du point et la position de l'onde
        float distanceToWave = abs(normalizedY - wavePosition);
        
        // Épaisseur de l'onde (plus petit = onde plus fine)
        float waveThickness = 0.15;
        
        // Intensité basée sur la proximité à l'onde
        float intensity = smoothstep(waveThickness, 0.0, distanceToWave);
        
        // Atténuer l'intensité quand l'onde monte (effet de fade)
        float fadeOut = smoothstep(waveDuration, 0.0, timeSinceClick);
        intensity *= fadeOut;
        
        return min(intensity, 1.0);
    }`;

  const nodeVertexShader = `
    ${sharedShaderCode}
    attribute vec3 color;
    varying vec3 vColor;
    varying float vPulseIntensity;
    #include <morphtarget_pars_vertex>
    void main() {
        vColor = color;
        vec3 transformed = vec3(position);
        #include <morphtarget_vertex>
        vec3 worldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
        vPulseIntensity = getPulseIntensity(worldPos);
        float pointSize = 1.0 + vPulseIntensity * 2.0;
        vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
        gl_PointSize = pointSize * (10.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
    }`;

  const nodeFragmentShader = `
    varying vec3 vColor;
    varying float vPulseIntensity;
    void main() {
        float dist = length(gl_PointCoord - vec2(0.5));
        if (dist > 0.5) discard;
        vec3 finalColor = vColor;
        if (vPulseIntensity > 0.0) {
            finalColor = mix(vColor, vec3(1.0), vPulseIntensity);
            finalColor *= (1.0 + vPulseIntensity * 0.5);
        }
        float alpha = (1.0 - dist * 2.0) * (1.0 + vPulseIntensity);
        gl_FragColor = vec4(finalColor, alpha);
    }`;

  const connectionVertexShader = `
    ${sharedShaderCode}
    attribute vec3 color;
    varying vec3 vColor;
    varying float vPulseIntensity;
    #include <morphtarget_pars_vertex>
    void main() {
        vColor = color;
        vec3 transformed = vec3(position);
        #include <morphtarget_vertex>
        vec3 worldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
        vPulseIntensity = getPulseIntensity(worldPos);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
    }`;

  const connectionFragmentShader = `
    varying vec3 vColor;
    varying float vPulseIntensity;
    void main() {
        vec3 finalColor = vColor;
        if (vPulseIntensity > 0.0) {
            finalColor = mix(vColor, vec3(1.0), vPulseIntensity);
            finalColor *= (1.0 + vPulseIntensity * 0.5);
        }
        gl_FragColor = vec4(finalColor, 0.3 + vPulseIntensity * 0.7);
    }`;

  const { nodeGeometry, connectionGeometry, nodeMaterial, connectionMaterial } = useMemo(() => {
    // Créer la géométrie de base (même scale que dans l'original HTML)
    const geometryScale = 12;
    const baseGeometry = new THREE.IcosahedronGeometry(geometryScale, 3);
    const sphereVertices: THREE.Vector3[] = [];
    const spherePositions = baseGeometry.attributes.position.array;

    // Extraire les sommets uniques
    const uniqueVerticesMap = new Map<string, THREE.Vector3>();
    for (let i = 0; i < spherePositions.length; i += 3) {
      const key = `${spherePositions[i].toFixed(3)},${spherePositions[i+1].toFixed(3)},${spherePositions[i+2].toFixed(3)}`;
      if (!uniqueVerticesMap.has(key)) {
        const vertex = new THREE.Vector3(spherePositions[i], spherePositions[i+1], spherePositions[i+2]);
        uniqueVerticesMap.set(key, vertex);
        sphereVertices.push(vertex);
      }
    }

    // Créer la géométrie des arêtes
    const edgeGeometry = new THREE.EdgesGeometry(baseGeometry, 1);
    const sphereConnectionPositions = new Float32Array(edgeGeometry.attributes.position.array);
    
    // Appliquer l'effet de spike aux connexions
    const tempVec = new THREE.Vector3();
    for (let i = 0; i < sphereConnectionPositions.length; i += 3) {
      tempVec.set(sphereConnectionPositions[i], sphereConnectionPositions[i+1], sphereConnectionPositions[i+2]);
      const spherical = new THREE.Spherical().setFromVector3(tempVec);
    //   const spikeFactor = 0.4 * Math.sin(spherical.phi * 6) * Math.sin(spherical.theta * 6);
      const spikeFactor = 0.;
      spherical.radius *= 1 + spikeFactor;
      tempVec.setFromSpherical(spherical);
      sphereConnectionPositions[i] = tempVec.x;
      sphereConnectionPositions[i+1] = tempVec.y;
      sphereConnectionPositions[i+2] = tempVec.z;
    }

    // Créer la géométrie des nœuds
    const sphereNodePositions = sphereVertices.flatMap(v => [v.x, v.y, v.z]);
    const nodeGeometry = new THREE.BufferGeometry();
    nodeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(sphereNodePositions, 3));
    
    // Créer les couleurs des nœuds
    const nodePalette = [new THREE.Color(0xDDEEFF)];
    const nodeColors = new Float32Array(sphereNodePositions.length);
    for (let i = 0; i < sphereNodePositions.length / 3; i++) {
      const color = nodePalette[Math.floor(Math.random() * nodePalette.length)];
      nodeColors[i*3] = color.r;
      nodeColors[i*3+1] = color.g;
      nodeColors[i*3+2] = color.b;
    }
    nodeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(nodeColors, 3));

    // Créer la géométrie des connexions
    const connectionGeometry = new THREE.BufferGeometry();
    connectionGeometry.setAttribute('position', new THREE.Float32BufferAttribute(sphereConnectionPositions, 3));
    
    // Créer les couleurs des connexions
    const connectionColors: number[] = [];
    const connectionPalette = [new THREE.Color(0x88DDFF)];
    for(let i = 0; i < sphereConnectionPositions.length / 6; i++) {
      const color = connectionPalette[Math.floor(Math.random() * connectionPalette.length)];
      connectionColors.push(color.r, color.g, color.b, color.r, color.g, color.b);
    }
    connectionGeometry.setAttribute('color', new THREE.Float32BufferAttribute(connectionColors, 3));

    // Créer les matériaux
    const pulseUniforms = {
      uTime: { value: 0.0 },
      uPulseSpeed: { value: 5.0 },
    };

    const nodeMaterial = new THREE.ShaderMaterial({
      uniforms: pulseUniforms,
      vertexShader: nodeVertexShader,
      fragmentShader: nodeFragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const connectionMaterial = new THREE.ShaderMaterial({
      uniforms: pulseUniforms,
      vertexShader: connectionVertexShader,
      fragmentShader: connectionFragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    return {
      nodeGeometry,
      connectionGeometry,
      nodeMaterial,
      connectionMaterial
    };
  }, []);

  // Mettre à jour le temps dans useFrame
  useFrame((state, delta) => {

    timeRef.current += delta;

    if(state)

    if (nodeMaterial && connectionMaterial) {
      nodeMaterial.uniforms.uTime.value = timeRef.current;
      connectionMaterial.uniforms.uTime.value = timeRef.current;
    }
  });

  return (
    <group scale={scale} quaternion={quaternion}>
      <points ref={nodesRef} geometry={nodeGeometry} material={nodeMaterial} frustumCulled={false} />
      <lineSegments ref={connectionsRef} geometry={connectionGeometry} material={connectionMaterial} />
    </group>
  );
};

export default WireframeShaderPlanet;

